import { Children, isValidElement } from "react";
import type { ChangeEvent, ReactNode } from "react";
import SelectMenu from "./SelectMenu";
import type { SelectMenuOption } from "./SelectMenu";

type SelectSize = "sm" | "md";

type SelectProps = {
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  children: ReactNode;
  size?: SelectSize;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
};

function optionLabel(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(optionLabel).join("");
  return "";
}

function toOptions(children: ReactNode): SelectMenuOption[] {
  const opts: SelectMenuOption[] = [];
  Children.toArray(children).forEach((child) => {
    if (!isValidElement(child) || child.type !== "option") return;
    const props = child.props as { value?: string | number; children?: ReactNode };
    const value = props.value !== undefined ? String(props.value) : optionLabel(props.children);
    opts.push({ value, label: optionLabel(props.children) || value });
  });
  return opts;
}

export default function Select({
  value,
  onChange,
  children,
  size = "md",
  className = "",
  disabled,
  placeholder,
  "aria-label": ariaLabel,
}: SelectProps) {
  return (
    <SelectMenu
      value={value !== undefined ? String(value) : ""}
      onChange={(v) => onChange?.({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>)}
      options={toOptions(children)}
      size={size}
      className={className}
      disabled={disabled}
      placeholder={placeholder ?? "Select..."}
      aria-label={ariaLabel}
    />
  );
}
